// The two textures the engine owns: the per-cell data textures and the
// sprite atlas (the plan's A.2 `gl/texture.ts`).
//
// The data texture is the reason the fast client has no retrace. One
// sovereignty flip writes one byte and marks the texture dirty; the frame
// loop then does ONE `texSubImage2D` of the whole 20 KB plane. That mirrors
// the "mark, do not rebuild" rule R109 gave the PixiJS client, except that
// the rebuild it avoids is now a 134 MB texture render.

import { CellPlane } from '../gfx/plane';
import type { GL, GpuResource } from './context';
import type {
  AtlasTexture as AtlasTextureApi, DataFormat, DataTexture as DataTextureApi,
} from '../gfx/types';

/** Binds one texture on one unit: the one way a plane or a sheet is read. */
export function bindTextureUnit(gl: WebGL2RenderingContext, unit: number, t: WebGLTexture): void {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, t);
}

interface FormatTriple { internal: number; format: number; type: number }

function tripleFor(gl: WebGL2RenderingContext, f: DataFormat): FormatTriple {
  switch (f) {
    case 'r8ui': return { internal: gl.R8UI, format: gl.RED_INTEGER, type: gl.UNSIGNED_BYTE };
    case 'r8': return { internal: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE };
    case 'r16f': return { internal: gl.R16F, format: gl.RED, type: gl.FLOAT };
    case 'rg16f': return { internal: gl.RG16F, format: gl.RG, type: gl.FLOAT };
    case 'rgba16f': return { internal: gl.RGBA16F, format: gl.RGBA, type: gl.FLOAT };
  }
}

/**
 * One plane of per-cell data, read by `texelFetch` on the integer cell
 * index. Nothing here is ever filtered on an integer format, because a
 * LINEAR integer texture samples black without an error. The CPU half is
 * the `CellPlane` of `gfx/plane.ts`.
 */
export class DataTexture extends CellPlane implements GpuResource, DataTextureApi {
  texture: WebGLTexture | null = null;
  /** The GL enums of the format, read once: an upload allocates nothing. */
  private readonly triple: FormatTriple;

  constructor(
    private readonly glc: GL,
    name: string,
    width: number,
    height: number,
    format: DataFormat,
    /** LINEAR is only legal on a float format. The default is NEAREST. */
    linear = false,
  ) {
    super(name, width, height, format, linear);
    this.triple = tripleFor(glc.gl, format);
    glc.register(this);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const t = gl.createTexture();
    if (!t) throw new Error(`fast: cannot create texture ${this.name}`);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texStorage2D(gl.TEXTURE_2D, 1, this.triple.internal, this.width, this.height);
    const f = this.linear ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.texture = t;
    this.markDirty();
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.texture) gl.deleteTexture(this.texture);
    this.texture = null;
  }

  /**
   * One `texSubImage2D` of the whole plane, and only when it is dirty. A
   * plane that `markRows` marked copies those rows alone (R2897).
   */
  upload(): boolean {
    if (!this.dirty || !this.texture || this.glc.lost) return false;
    const gl = this.glc.gl;
    const { format, type } = this.triple;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    if (this.rowLo >= 0) {
      const r0 = this.upRow;
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, r0, this.width, this.upRows, format, type, this.data,
        r0 * this.width * this.channels);
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, format, type, this.data);
    }
    this.uploaded();
    return true;
  }

  bind(unit: number): void {
    if (!this.texture || this.glc.lost) return;
    bindTextureUnit(this.glc.gl, unit, this.texture);
  }

  dispose(): void { this.glc.unregister(this); }
}

/** Mip level 3 is the atlas floor: an eighth of the cell, and 8 texels of
 *  padding still leave one texel there. Anything smaller bleeds. */
export const ATLAS_MAX_LEVEL = 3;

/**
 * The sprite atlas: one RGBA8 texture built from a 2D canvas, uploaded
 * premultiplied so the one blend state fits it, with four mip levels.
 */
export class AtlasTexture implements GpuResource, AtlasTextureApi {
  texture: WebGLTexture | null = null;
  private source: HTMLCanvasElement;

  constructor(private readonly glc: GL, source: HTMLCanvasElement) {
    this.source = source;
    glc.register(this);
  }

  /** Points the texture at another canvas, for instance after a grow. */
  setSource(source: HTMLCanvasElement): void {
    this.source = source;
    if (!this.glc.lost) this.create(this.glc.gl);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const t = gl.createTexture();
    if (!t) throw new Error('fast: cannot create the atlas texture');
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, this.source);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, ATLAS_MAX_LEVEL);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.texture = t;
  }

  /**
   * Uploads one rectangle again. The badge digits use it: the face lands
   * after the first bake, and the cell is redrawn in place.
   *
   * A restore needs no replay: the atlas draws every patch into the source
   * canvas before it calls here, so `create` uploads them with the sheet.
   */
  patch(canvas: HTMLCanvasElement, x: number, y: number): void {
    if (!this.texture || this.glc.lost) return;
    const gl = this.glc.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.generateMipmap(gl.TEXTURE_2D);
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.texture) gl.deleteTexture(this.texture);
    this.texture = null;
  }

  bind(unit: number): void {
    if (!this.texture || this.glc.lost) return;
    bindTextureUnit(this.glc.gl, unit, this.texture);
  }

  dispose(): void { this.glc.unregister(this); }
}
